<template>
  <div :class="cardClass" class="card">
    <div class="row gx-0" :class="rowClass">
      <div
        class="col-12 col-md-5 position-relative cih-bg-image cih-lazy"
        :data-background-image="props.bgImageSource">
        <div v-if="displayLogo" class="logo-container">
          <img :src="logoSource" :alt="logoAlt" class="cih-logo" />
        </div>
      </div>
      <div
        class="col-12 col-md-7"
        :class="cardBodyClass"
        :style="cardBodyStyle">
        <div class="card-body cih-body p-space-sm p-lg-space-md">
          <!-- label -->
          <p
            v-if="displayLabel"
            class="mb-space-xs p-space-xxs d-inline-block"
            :class="labelClass"
            v-html="labelText"></p>

          <!-- title -->
          <component
            :is="titleLevel"
            class="mb-space-sm mb-lg-space-sm"
            :class="titleClass">
            {{ title }}
          </component>

          <!-- slot -->
          <div>
            <slot />
          </div>

          <!-- cta -->
          <div v-if="displayCta" class="row">
            <div class="col-12">
              <a
                :target="ctaLinkTarget"
                class="mb-0"
                :class="ctaClass"
                :href="ctaLink"
                :aria-label="ctaText"
                @click="handleClick"
                >{{ ctaText }}</a
              >
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import lozad from "lozad";
import { computed, onMounted } from "vue";

interface Props {
  borderVariant?: string;
  bgImageSource: string;
  imagePosition?: "right" | "left";
  displayLogo?: boolean;
  logoSource?: string;
  logoAlt?: string;
  displayHighlight?: boolean;
  highlightColor?: string;
  highlightWidth?: number;
  mobileHighlightLocation?: "right" | "left" | "top" | "bottom";
  displayLabel?: boolean;
  labelBg?: string;
  labelText?: string;
  labelTextSize?: string;
  labelTextVariant?: string;
  title: string;
  titleLevel?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  titleSize?: string;
  titleVariant?: string;
  displayCta?: boolean;
  ctaAsButton?: boolean;
  ctaButtonVariant?: string;
  ctaText?: string;
  ctaTextSize?: string;
  ctaTextVariant?: string;
  ctaTextWeight?: string;
  ctaLink?: string;
  ctaLinkTarget?: "_blank" | "_parent" | "_self" | "_top";
}

const props = withDefaults(defineProps<Props>(), {
  borderVariant: "white",
  imagePosition: "left",
  displayLogo: false,
  logoSource: "https://via.placeholder.com/200x36?text=logoSource",
  logoAlt: "cih logo alt",
  displayHighlight: true,
  highlightColor: "var( --rds-secondary, #ffc627)",
  highlightWidth: 5,
  mobileHighlightLocation: "left",
  displayLabel: true,
  labelBg: "secondary",
  labelText: "lorem ipsum",
  labelTextSize: "xs",
  labelTextVariant: "dark-3",
  titleLevel: "h3",
  titleSize: "medium",
  titleVariant: "dark-3",
  displayCta: true,
  ctaAsButton: false,
  ctaButtonVariant: "primary",
  ctaText: "cta text",
  ctaTextSize: "large",
  ctaTextVariant: "primary",
  ctaTextWeight: "normal",
  ctaLink: "https://asuonline.asu.edu/",
  ctaLinkTarget: "_blank",
});

/** Events */
const emits = defineEmits<{
  /**
   *  Emitted when the cta is clicked.
   */
  (e: "ctaClick", event: Event): void;
}>();

const getColor = (color: string) => {
  if (color.includes("var(")) {
    return `${color}`;
  } else if (color.includes("--")) {
    return `var(${color})`;
  } else {
    return color;
  }
};

const cardClass = computed(() => {
  return `border border-${props.borderVariant}`;
});

const rowClass = computed(() => {
  return props.imagePosition == "left" ? `image-side-left` : `image-side-right`;
});

const cardBodyClass = computed(() => {
  return props.mobileHighlightLocation == props.imagePosition
    ? null
    : `mobile-highlight-${props.mobileHighlightLocation}`;
});

const cardBodyStyle = computed(() => {
  if (props.displayHighlight) {
    return `
      border-${
        props.imagePosition
      }:var(--highlight-width) solid var(--highlight-color);
      --highlight-color:${getColor(props.highlightColor)};
      --highlight-width:${props.highlightWidth}px;
      `;
  }
  return "";
});

const labelClass = computed(() => {
  return `bg-${props.labelBg} fs-${props.labelTextSize} text-${props.labelTextVariant}`;
});

const titleClass = computed(() => {
  return `${props.titleLevel}-${props.titleSize} text-${props.titleVariant}`;
});

const ctaClass = computed(() => {
  let classNames = "";

  if (props.ctaAsButton) {
    classNames += `btn btn-${props.ctaButtonVariant} `;
  } else {
    classNames += "rds-link ";
  }

  classNames += `fs-${props.ctaTextSize} text-${props.ctaTextVariant} fw-${props.ctaTextWeight} `;

  return classNames;
});

const handleClick = (event: Event) => {
  emits("ctaClick", event);
};

onMounted(() => {
  const observer = lozad(".cih-lazy");
  observer.observe();
});
</script>

<style lang="scss" scoped>
.cih-bg-image {
  background-repeat: no-repeat;
  background-size: cover;
  background-position: center top;
  height: 360px;
}

div.logo-container {
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 60%;
  background: transparent linear-gradient(360deg, #000000 0%, #54545400 100%) 0%
    0% no-repeat padding-box;
  opacity: 1;
}
img.cih-logo {
  position: absolute;
  max-width: 100%;
  bottom: 24px;
  padding-left: 12px;
}

div.cih-body {
  padding: 0;
}
@media (max-width: 767px) {
  .mobile-highlight-top {
    border-top: var(--highlight-width) solid var(--highlight-color);
    border-bottom: none !important;
    border-left: none !important;
    border-right: none !important;
  }
  .mobile-highlight-bottom {
    border-bottom: var(--highlight-width) solid var(--highlight-color);
    border-top: none !important;
    border-left: none !important;
    border-right: none !important;
  }
}

/* Medium devices (tablets, 768px and up) */
@media (min-width: 768px) {
  .cih-bg-image {
    height: auto;
  }
  .image-side-right {
    flex-direction: row-reverse;
  }
}
</style>
